"use client";

import { useCallback, useEffect, useState } from "react";

const CURRENT_BUILD_ID = process.env.NEXT_PUBLIC_APP_BUILD_ID;
const POLL_INTERVAL_MS = 5 * 60 * 1000;

export interface NewBuild {
  version: string | null;
  buildId: string;
}

/**
 * Polls /api/version (answered by whichever deployment is live) and reports
 * when it differs from the build this tab is running.
 *
 * Returns { version, buildId } of the newer build, or null. Off in dev and
 * fails quiet on any network error.
 */
export function useNewBuildAvailable(): NewBuild | null {
  const [newBuild, setNewBuild] = useState<NewBuild | null>(null);

  const check = useCallback(async () => {
    if (process.env.NODE_ENV !== "production" || !CURRENT_BUILD_ID) return;
    try {
      const res = await fetch(`/api/version?t=${Date.now()}`, {
        cache: "no-store",
      });
      if (!res.ok) return;
      const data = await res.json();
      if (data?.buildId && data.buildId !== CURRENT_BUILD_ID) {
        setNewBuild({ version: data.version ?? null, buildId: data.buildId });
      }
    } catch {
      // Offline or blocked: try again on the next tick.
    }
  }, []);

  useEffect(() => {
    const first = setTimeout(check, 0);
    const id = setInterval(check, POLL_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", check);
    return () => {
      clearTimeout(first);
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", check);
    };
  }, [check]);

  return newBuild;
}
